import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Target,
  Building,
  MapPin,
  Calendar,
  DollarSign,
  Clock,
  Plus,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  Send,
  User,
  Briefcase
} from "lucide-react";
import axios from "../../axios/AxiosInstance";
import { toast } from "react-toastify";

const OpportunitiesSection = ({ candidateId }) => {
  const [opportunities, setOpportunities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  useEffect(() => {
    loadOpportunities();
  }, [candidateId]);

  const loadOpportunities = async () => {
    try {
      setIsLoading(true);
      // Simuler des données d'opportunités pour l'instant
      // TODO: Remplacer par l'API réelle
      const mockOpportunities = [
        {
          id: 1,
          title: "Développeur Full Stack",
          client: "TechCorp Solutions",
          location: "Paris 15ème",
          startDate: "2024-02-01",
          endDate: "2024-06-30",
          salary: "450€/jour",
          status: "En attente",
          description: "Développement d'une application web moderne avec React et Node.js",
          requirements: ["React", "Node.js", "MongoDB", "3+ ans d'expérience"],
          contactPerson: "Sophie Martin",
          sentDate: "2024-01-10",
          responseDate: null
        },
        {
          id: 2,
          title: "Chef de Projet IT",
          client: "Digital Innovations",
          location: "Lyon",
          startDate: "2024-01-20",
          endDate: "2024-12-31",
          salary: "600€/jour",
          status: "Accepté",
          description: "Pilotage de projets de transformation digitale",
          requirements: ["Gestion de projet", "Agile", "Leadership", "5+ ans d'expérience"],
          contactPerson: "Marc Dubois",
          sentDate: "2024-01-05",
          responseDate: "2024-01-08"
        },
        {
          id: 3,
          title: "Consultant SAP",
          client: "Enterprise Systems",
          location: "Remote",
          startDate: "2024-03-01",
          endDate: "2024-08-31",
          salary: "550€/jour",
          status: "Refusé",
          description: "Implémentation et configuration de modules SAP",
          requirements: ["SAP", "ABAP", "Functional", "4+ ans d'expérience"],
          contactPerson: "Claire Rousseau",
          sentDate: "2024-01-12",
          responseDate: "2024-01-15"
        }
      ];
      
      setTimeout(() => {
        setOpportunities(mockOpportunities);
        setIsLoading(false);
      }, 500);
    } catch (error) {
      console.error("Erreur lors du chargement des opportunités:", error);
      toast.error("Erreur lors du chargement des opportunités");
      setIsLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "En attente":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "Accepté":
        return "bg-green-100 text-green-800 border-green-200";
      case "Refusé":
        return "bg-red-100 text-red-800 border-red-200";
      case "Envoyé":
        return "bg-blue-100 text-blue-800 border-blue-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "En attente":
        return <Clock className="w-4 h-4" />;
      case "Accepté":
        return <CheckCircle className="w-4 h-4" />;
      case "Refusé":
        return <XCircle className="w-4 h-4" />;
      case "Envoyé":
        return <Send className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR");
  };

  const calculateDuration = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const months = Math.floor(diffDays / 30);
    const days = diffDays % 30;
    
    if (months > 0) {
      return `${months} mois${days > 0 ? ` et ${days} jours` : ''}`;
    }
    return `${days} jours`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header avec bouton d'ajout */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Opportunités</h2>
          <p className="text-gray-600 mt-1">
            Missions et postes proposés au candidat
          </p>
        </div>
        <motion.button
          onClick={() => setIsAddModalOpen(true)}
          className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Plus className="w-4 h-4" />
          <span>Nouvelle opportunité</span>
        </motion.button>
      </div>

      {/* Statistiques rapides */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Send className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-900">Envoyées</span>
          </div>
          <p className="text-2xl font-bold text-blue-900 mt-1">
            {opportunities.filter(o => o.status === "Envoyé" || o.status === "En attente").length}
          </p>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-sm font-medium text-green-900">Acceptées</span>
          </div>
          <p className="text-2xl font-bold text-green-900 mt-1">
            {opportunities.filter(o => o.status === "Accepté").length}
          </p>
        </div>
        <div className="bg-red-50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <XCircle className="w-5 h-5 text-red-600" />
            <span className="text-sm font-medium text-red-900">Refusées</span>
          </div>
          <p className="text-2xl font-bold text-red-900 mt-1">
            {opportunities.filter(o => o.status === "Refusé").length}
          </p>
        </div>
        <div className="bg-yellow-50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Clock className="w-5 h-5 text-yellow-600" />
            <span className="text-sm font-medium text-yellow-900">En attente</span>
          </div>
          <p className="text-2xl font-bold text-yellow-900 mt-1">
            {opportunities.filter(o => o.status === "En attente").length}
          </p>
        </div>
      </div>

      {/* Liste des opportunités */}
      {opportunities.length === 0 ? (
        <motion.div
          className="text-center py-12 bg-gray-50 rounded-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Aucune opportunité
          </h3>
          <p className="text-gray-600 mb-4">
            Commencez par proposer une mission à ce candidat
          </p>
          <motion.button
            onClick={() => setIsAddModalOpen(true)}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            whileHover={{ scale: 1.02 }}
          >
            Proposer une mission
          </motion.button>
        </motion.div>
      ) : (
        <div className="grid gap-6">
          {opportunities.map((opportunity, index) => (
            <motion.div
              key={opportunity.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Briefcase className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {opportunity.title}
                    </h3>
                    <p className="text-gray-600 flex items-center space-x-1">
                      <Building className="w-4 h-4" />
                      <span>{opportunity.client}</span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(opportunity.status)}`}>
                    <div className="flex items-center space-x-1">
                      {getStatusIcon(opportunity.status)}
                      <span>{opportunity.status}</span>
                    </div>
                  </span>
                  <div className="flex space-x-1">
                    <motion.button
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      whileHover={{ scale: 1.1 }}
                    >
                      <Eye className="w-4 h-4" />
                    </motion.button>
                    <motion.button
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      whileHover={{ scale: 1.1 }}
                    >
                      <Edit className="w-4 h-4" />
                    </motion.button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div className="flex items-center space-x-2 text-gray-600">
                  <MapPin className="w-4 h-4" />
                  <span>{opportunity.location}</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(opportunity.startDate)}</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-600">
                  <Clock className="w-4 h-4" />
                  <span>{calculateDuration(opportunity.startDate, opportunity.endDate)}</span>
                </div>
                <div className="flex items-center space-x-2 text-gray-600">
                  <DollarSign className="w-4 h-4" />
                  <span className="font-medium">{opportunity.salary}</span>
                </div>
              </div>

              <div className="mb-4">
                <p className="text-gray-700 text-sm">{opportunity.description}</p>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                {opportunity.requirements.map((req, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-md"
                  >
                    {req}
                  </span>
                ))}
              </div>

              <div className="flex items-center justify-between text-sm text-gray-500">
                <div className="flex items-center space-x-2">
                  <User className="w-4 h-4" />
                  <span>Contact: {opportunity.contactPerson}</span>
                </div>
                <div>
                  Envoyé le {formatDate(opportunity.sentDate)}
                  {opportunity.responseDate && (
                    <span> • Réponse le {formatDate(opportunity.responseDate)}</span>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modal d'ajout (placeholder) */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Nouvelle opportunité</h3>
            <p className="text-gray-600 mb-4">
              Fonctionnalité en cours de développement...
            </p>
            <button
              onClick={() => setIsAddModalOpen(false)}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
            >
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OpportunitiesSection;
